import React from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { setMenuState } from "src/store/Goal-Based/MenuSlice";
import { RootStateType } from "src/store/rootReducer";
import { SegmentedControl } from "src/components/ui";
import { colors } from "src/theme/colors";
import { space } from "src/theme/ui";

/**
 * Which of the four numbers the planner is working out.
 *
 * It had been an Ant Design tab strip on a lavender bar, each tab carrying a
 * 48px cartoon — two copies of every one, a PNG and an SVG, swapped on
 * selection — and labels long enough that the fourth was cut off behind an
 * overflow "…" at any width narrower than a desk monitor. A tab bar scrolls;
 * it does not wrap, which is why the last choice simply went missing.
 *
 * It is a segmented control now, because that is what it does: this SETS a
 * value rather than navigating, and the product already has one shape for
 * that. And the four labels are the four labels from the cards that lead
 * here — "Contribution", not "Goal Amount" for the mode that solves the
 * contribution — so a reader who picked a card finds the same word selected
 * on arrival. They were different words before, and two of them named the
 * opposite quantity.
 */

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${space.sm}px ${space.md}px;
  margin-bottom: ${space.lg}px;
`;

/* Said once, beside the control, rather than repeated inside all four
   labels — which is what made them too long to fit. */
const Label = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
`;

/* The same order as the cards that lead here — goal, contribution, initial,
   duration — so the segment that lights up is in the position the reader
   just clicked. The store's names are the other way round for two of them:
   the mode called GoalAmount is the one that SOLVES the contribution. */
const MODES = [
  { value: "ContributionAmount", label: "Goal amount" },
  { value: "GoalAmount", label: "Contribution" },
  { value: "SolveInitial", label: "Initial amount" },
  { value: "SolveDuration", label: "Duration" },
];

const TopHeader = () => {
  const menuState = useSelector((state: RootStateType) => state.menuReducer);
  const dispatch = useDispatch();

  return (
    <Bar>
      <Label>Solve for</Label>
      <SegmentedControl
        aria-label="What to solve for"
        options={MODES}
        value={menuState.currentState}
        onChange={(next) => dispatch(setMenuState({ currentState: next }))}
      />
    </Bar>
  );
};

export default TopHeader;
